import type { DateRange } from "@/shared/components/date-picker";
import {
	DATE_ONLY_FORMAT,
	DateRangePicker,
} from "@/shared/components/date-picker";
import { getMomentTz, MesfModal } from "@dexteel/mesf-core";
import { Button, DialogContentText, Typography } from "@mui/material";
import { useState } from "react";
import {
	type BillPeriod,
	findOverlappingPeriod,
	getBilledDays,
} from "../../../shared/utils/billPeriods";
import { toUtcDay } from "../../../shared/utils/date";

const moment = getMomentTz();

interface NewWaterBillDialogProps {
	open: boolean;
	accountLabel: string;
	meterCount: number;
	periods: BillPeriod[];
	isSaving: boolean;
	onClose: () => void;
	onCreate: (period: {
		ServicePeriodStart: Date;
		ServicePeriodEnd: Date;
	}) => Promise<boolean>;
}

export const NewWaterBillDialog = ({
	open,
	accountLabel,
	meterCount,
	periods,
	isSaving,
	onClose,
	onCreate,
}: NewWaterBillDialogProps) => {
	const [range, setRange] = useState<DateRange>({ start: null, end: null });

	const billedDays = getBilledDays(periods);
	const overlapping = findOverlappingPeriod(
		periods,
		toUtcDay(range.start),
		toUtcDay(range.end),
		null,
	);

	const start = overlapping?.start
		? moment.utc(overlapping?.start).format("MM/DD/YY")
		: "";
	const end = overlapping?.end
		? moment.utc(overlapping?.end).format("MM/DD/YY")
		: "";

	const canCreate =
		range.start !== null &&
		range.end !== null &&
		overlapping === null &&
		!isSaving;

	const close = () => {
		setRange({ start: null, end: null });
		onClose();
	};

	return (
		<MesfModal open={open} handleClose={close} maxWidth="sm" title="New bill">
			<MesfModal.Content dividers>
				<DialogContentText sx={{ fontSize: "0.85rem", mb: 2 }}>
					{accountLabel} · {meterCount} {meterCount === 1 ? "meter" : "meters"}{" "}
					will be added to the bill with the account's default split.
				</DialogContentText>
				<DateRangePicker
					value={range}
					onChange={setRange}
					format={DATE_ONLY_FORMAT.LONG}
					labels={{ start: "Start date", end: "End date" }}
					shouldDisableDate={(day) => billedDays.has(day)}
					allowDisabledInside
					clearable
					fullWidth
				/>
				{overlapping !== null && (
					<Typography sx={{ color: "error.main", fontSize: "0.75rem", mt: 1 }}>
						Overlaps the bill of {start} → {end}
					</Typography>
				)}
			</MesfModal.Content>
			<MesfModal.Actions>
				<Button onClick={close} color="inherit">
					Cancel
				</Button>
				<Button
					variant="contained"
					disabled={!canCreate}
					onClick={async () => {
						const start = toUtcDay(range.start);
						const end = toUtcDay(range.end);
						if (start === null || end === null) return;

						const created = await onCreate({
							ServicePeriodStart: start,
							ServicePeriodEnd: end,
						});

						if (created) close();
					}}
				>
					Create
				</Button>
			</MesfModal.Actions>
		</MesfModal>
	);
};
